import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DocketAPIError, getAPIClient } from "../../apiClient";
import { ErrorModal } from "../../components/modals/ErrorModal";
import { izSearchAPIResultAtom, selectedSearchAtom } from "../../jotai/atoms";
import { useAtom } from "jotai";
import { fetchRecordsUntilUpdated } from "../../utils/polling";
import { errorLog } from "../../utils/logger";

interface EnterPinFormProps {
  onVerify?: () => void;
}

export function EnterPinForm({ onVerify }: EnterPinFormProps) {
  const [_izSearchAPIResult, setIzSearchAPIResult] = useAtom(izSearchAPIResultAtom);
  const [search, setSearch] = useAtom(selectedSearchAtom);
  const [showErrorModal, setShowErrorModal] = useState<boolean | string>(false);
  const [pin, setPin] = useState("");

  const { t } = useTranslation();

  const mobilePinInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (mobilePinInputRef.current) {
      mobilePinInputRef.current.focus();
    }
  }, []);

  const getInfoText = () => {
    return t("immunizations.pin_entry_header2_alt").split("|");
  };

  const continueFromAlert = async () => {
    if (pin.length < 8 || search === null) {
      return;
    }

    try {
      await getAPIClient().verifyImmunizationSearchPIN(
        search.uid,
        { pin: pin },
        search.izProviderKey
      );
      /**
       * Do not await this since it could potentially take a long time to
       * complete and we want to close the modal immediately.
       */
      fetchRecordsUntilUpdated();
      onVerify?.();
    } catch (e: unknown) {
      if (e instanceof DocketAPIError) {
        setShowErrorModal(e.message);
      }
    }
  };

  const formatPin = (value: string) => {
    return value.replace(/^(\d{0,4})(\d{0,4})$/, (_, g1, g2) => (g2 ? `${g1}-${g2}` : g1));
  };

  const onPinChange = (value: string) => {
    let cleaned = value.replace(/\D/g, "");
    if (cleaned.length > 8) cleaned = cleaned.slice(0, 8);
    setPin(cleaned);
  };

  return (
    <div className="flex flex-col  gap-4 h-full">
      {showErrorModal && (
        <ErrorModal onCloseModal={() => setShowErrorModal(false)}>{showErrorModal}</ErrorModal>
      )}
      <div className="flex-1 overflow-y-auto flex flex-col gap-2 p-2">
        <div className="relative flex items-center justify-center w-full max-md:hidden">
          <p className="max-md:hidden text-center">{t("immunizations.pin_entry_header")}</p>
        </div>
        <input
          className="input input-primary input-xl w-full md:hidden text-lg"
          type="tel"
          inputMode="tel"
          pattern="[0-9]*"
          placeholder="Enter Code"
          data-testid="pin-mobile"
          maxLength={9}
          value={formatPin(pin)}
          onChange={(event) => onPinChange(event.target.value)}
          ref={mobilePinInputRef}
        />
        <input
          className="input input-primary w-full max-md:hidden px-2"
          type="numeric"
          placeholder="Enter Code"
          data-testid="pin-desktop"
          pattern="[0-9]*"
          maxLength={9}
          value={formatPin(pin)}
          onChange={(event) => onPinChange(event.target.value)}
        />
        <p className="md:hidden text-xl">{t("immunizations.pin_entry_header")}</p>
        <p>{getInfoText().join(" ")}</p>
        {/* Desktop button stays in content flow */}
        <button
          type="submit"
          data-testid="verify-desktop"
          className="btn w-fit max-md:hidden"
          onClick={() => continueFromAlert()}
          {...(pin.length < 8 && { disabled: true })}
        >
          {t("generic.verify")}
        </button>
      </div>
      {/* Mobile button anchored at bottom */}
      <div className="shrink-0 max-md:pb-8 md:hidden">
        <button
          type="submit"
          className="btn btn-lg btn-primary w-full"
          onClick={() => continueFromAlert()}
          {...(pin.length < 8 && { disabled: true })}
        >
          {t("generic.verify")}
        </button>
      </div>
    </div>
  );
}

export default EnterPinForm;
